import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
  Tooltip,
  Grid
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Business as BusinessIcon } from '@mui/icons-material';

import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import type { I18nScenarioEditorNs, I18nCommonNs } from '@/i18n/dictionaries/interfaces.ts';
import type { VariableSettingsDto } from '@/api-gen/api.schemas';
import useStyles from './styles';
import { useComputed, Signal } from '@preact/signals-react';

/** 
 * Displays a single scenario variable as a card
 */
interface VariableCardProps {
  variable: VariableSettingsDto;
  index: number;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  selectedCompanyId: Signal<number | null>;
  isShared: boolean;
}

const VariableCard: React.FC<VariableCardProps> = ({ variable, index, onEdit, onDelete, selectedCompanyId, isShared }) => {
  const [translate] = useTranslation([I18nNamespace.ScenarioEditor]);
  const [translateCommon] = useTranslation([I18nNamespace.Common]);
  const classes = useStyles();

  // Use the passed selectedCompanyId signal
  const companyValues = useComputed(() => 
    variable.values?.filter(v => v.companyId === selectedCompanyId.value) || []
  );

  const sharedValues = variable.values?.filter(v => v.companyId == null) || [];

  const hasSelectedCompanyValues = useComputed(() => companyValues.value.length > 0);
  const enableComapnySpecificValues = useComputed(() => hasSelectedCompanyValues.value && isShared);

  // Determine which values to display - prioritize company values for the selected company
  const displayValues = useComputed(() =>
    enableComapnySpecificValues.value ? companyValues.value : sharedValues
  );

  // Determine if both types of values exist for the *selected* company vs shared (for tooltip)
  const hasMultipleValueTypes = companyValues.value.length > 0 && sharedValues.length > 0;

  // Determine if the variable *has* any company-specific values for the *selected* company
  return (
    <Card className={classes.variableCard}>
      <CardContent className={classes.variableCardContent}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Box display="flex" alignItems="center">
            <Typography 
              variant="h6" 
              className={classes.variableCardTitle}
            >
              {variable.name}
            </Typography>
            {/* Show icon if displaying company-specific values for the selected company */}
            {enableComapnySpecificValues.value&& (
              <Tooltip title="Showing company-specific values for the selected company">
                <BusinessIcon 
                  fontSize="small" 
                  sx={{ 
                    ml: 1, 
                    color: 'primary.main',
                    opacity: 0.8,
                    fontSize: '0.9rem'
                  }} 
                />
              </Tooltip>
            )}
          </Box>
          <Box>
            <IconButton 
              onClick={() => onEdit(index)} 
              size="small"
              className={`${classes.actionButton} ${classes.editButton}`}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton 
              onClick={() => onDelete(index)} 
              size="small"
              className={`${classes.actionButton} ${classes.deleteButton}`}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        
        {variable.values && variable.values.length > 0 ? (
          <Tooltip 
            title={hasMultipleValueTypes ? "Showing company-specific values for the selected company (shared values also exist but are hidden)" : ""}
            placement="bottom"
            disableHoverListener={!hasMultipleValueTypes}
          >
            {/* Highlight background if displaying company-specific values */}
            <Box className={classes.variableValueContainer} sx={{ 
              bgcolor: hasSelectedCompanyValues.value ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
              borderRadius: 1
            }}>
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '4px',
                margin: '-2px'
              }}>
                {displayValues.value.map((val, i) => {
                  // Determine size based on content length
                  const length = val.value.length;

                  return (
                    <Box
                      key={i}
                      sx={{
                        flex: length < 8 ? '0 0 auto' : length < 15 ? '1 0 auto' : '2 0 auto',
                        minWidth: length < 8 ? '80px' : length < 15 ? '120px' : '180px',
                        maxWidth: '100%',
                        padding: '2px'
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        className={classes.variableValue}
                        sx={{ 
                          m: 0,
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          // Highlight individual value background if it's company-specific
                          backgroundColor: val.companyId === selectedCompanyId.value ? 'rgba(25, 118, 210, 0.06)' : undefined,
                          borderColor: val.companyId === selectedCompanyId.value ? 'rgba(25, 118, 210, 0.1)' : undefined
                        }}
                      >
                        {val.value}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Tooltip>
        ) : (
          <Typography 
            variant="body2" 
            className={classes.variableValueContainer}
            sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}
          >
            {variable.values?.[0]?.value || <em>No value</em>}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default VariableCard;
